import React from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components/macro';

import { ButtonLink } from '../../../components/Buttons';
import EmptyState from '../../../components/EmptyState';

import { P } from '../../../components/Typography';
import { distances } from '../../../styles/constants';

import { toMonetaryAmountString, safeDecodeURIComponent } from '../../../helpers/formatters';
import { DraftOrGap } from '../types';
import { TFunction } from 'i18next';
import NoData from '../../common/NoData';

export interface ListMessageProps extends WithTranslation {
    queryParams: URLSearchParams;
    drafts: DraftOrGap[];
    isLoading: boolean;
    isLoadingNext: boolean;
    accountId: string;
    hasNext: boolean;
    currency: string;
}

const getTranslation = (t: TFunction, key: string): string | undefined => {
    const translated = t(key);
    if (translated === key) {
        return;
    }
    return translated;
};

const getEmptyFilterStringPart = (
    t: TFunction,
    locationQueryParams: URLSearchParams,
    lng: string,
    currency: string
) => {
    let entries = [...locationQueryParams.entries()]
        .filter(([key]) => !['created_at.gte', 'created_at.lte', 'starting_after'].includes(key))
        .filter(([key]) => !['net_amount.gte', 'net_amount.lte'].includes(key))
        .filter(([key, value]) => (key !== 'card_brand' ? true : value !== 'null'))
        .filter(([key, value]) => (key !== 'payment_product_type' ? true : value !== 'payex.creditcard'))
        .map(([key, value]) => {
            if (key === 'status') {
                return getTranslation(t, `orders.order_statuses.${value || 'undefined'}`) || value;
            }
            if (key === 'payment_product_type') {
                return getTranslation(t, `orders.product_types.${value || 'undefined'}`) || value;
            }
            return safeDecodeURIComponent(value);
        });

    const fromDate = locationQueryParams.get('created_at.gte');
    const toDate = locationQueryParams.get('created_at.lte');
    if (fromDate && fromDate === toDate) {
        entries = [fromDate, ...entries];
    } else if (fromDate && toDate) {
        entries = [`${fromDate} – ${toDate}`, ...entries];
    } else if (fromDate || toDate) {
        entries = [`${toDate ? '<=' : '>='} ${toDate || fromDate} `, ...entries];
    }

    const amountMin = locationQueryParams.get('net_amount.gte');
    const amountMax = locationQueryParams.get('net_amount.lte');

    const minLabel = amountMin ? toMonetaryAmountString(lng, parseFloat(amountMin), currency) : '';
    const maxLabel = amountMax ? toMonetaryAmountString(lng, parseFloat(amountMax), currency) : '';
    if (minLabel && !maxLabel) {
        entries = [`${minLabel} \u2264`, ...entries];
    } else if (maxLabel && !minLabel) {
        entries = [`\u2264 ${maxLabel}`, ...entries];
    } else if (minLabel && maxLabel) {
        entries = [`${minLabel}-${maxLabel}`, ...entries];
    }
    return entries.join(', ');
};

const ListMessage = (
    {
        t,
        drafts,
        isLoading,
        accountId,
        queryParams,
        hasNext,
        isLoadingNext,
        i18n,
        currency,
    }: ListMessageProps
) => {
    if (isLoading || isLoadingNext) {
        return null;
    }

    const locationValues = getEmptyFilterStringPart(t, queryParams, i18n.language, currency);
    // The account has no drafts yet
    if (!isLoading && drafts.length === 0 && !locationValues) {
        return <NoData />;
    }

    const hasRangeFilter = queryParams.get('created_at.gte') && queryParams.get('created_at.lte');
    // Filtering drafts yields an empty result and date filter is set
    if (drafts.length === 0 && locationValues) {
        return (
            <EmptyState>
                <P>
                    <Trans i18nKey="orders.drafts.empty_filtered_list.message" values={{ locationValues }}>
                        Sorry, we can't find any draft orders matching <strong>{locationValues}</strong>.
                    </Trans>
                </P>
                {!hasRangeFilter && (
                    <P>
                        <br />
                        {t('orders.drafts.implicit_three_month_limit')}
                    </P>
                )}
                <ButtonLink to={`/${accountId}/orders/orders`}>
                    {t('orders.drafts.empty_filtered_list.clear_search_and_filters')}
                </ButtonLink>
            </EmptyState>
        );
    }

    // User is filtering draft orders without a date filter and is on the last page of the results
    if (!isLoadingNext && !hasNext && locationValues && !hasRangeFilter) {
        return (
            <SlideMessage>
                <BelowPaging>
                    <P>{t('orders.drafts.implicit_three_month_limit')}</P>
                </BelowPaging>
            </SlideMessage>
        );
    }

    return null;
};

const BelowPaging = styled.section`
    padding: ${distances.large};
    padding-top: ${distances.small};
    text-align: center;
    line-height: ${distances.ginormous};
`;

const slideFromTop = keyframes`
  from {
      max-height: 0;
      opacity: 0;
  }

  to {
    max-height: 144px;
    opacity: 1;
  }
`;

const SlideMessage = styled.div`
    overflow: hidden;
    animation: ${slideFromTop} 200ms ease-out;
`;

export default withTranslation()(ListMessage);
